import { KmsTypeCategoryMembership, KmsTypePublishCategory, CategoryType } from "@kaltura/mediaspace-shared-types";
import { Tooltip } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import {
    Expert24Icon,
    Globe24Icon,
    Group24Icon,
    Lock24Icon,
    NoAccess24Icon,
    Unlock24Icon,
    Visible24Icon,
} from "@kaltura/ds-react-icons";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { getMembershipTooltip } from "../../../../../../utils";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { ReactElement } from "react";

interface CategoryIconProps {
    category: KmsTypePublishCategory;
    categoryType: CategoryType.channel | CategoryType.gallery;
    isBulk?: boolean;
    analyticsActionName?: string;
}

/**
 * Category membership and moderation icons
 */
export function CategoryIcon({ category, categoryType, isBulk, analyticsActionName }: CategoryIconProps) {
    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = () => {
        const actionName =
            analyticsActionName
            ?? `Publish - Set publishing settings modal box - Info for ${categoryType} ${isBulk ? "(bulk)" : ""}`;
        sendButtonAnalytics(actionName, ButtonClickAnalyticsType.EXPAND);
    };

    const getIconWithTooltip = (icon: ReactElement, text: string) => (
        <Tooltip onOpen={sendAnalytics} title={HtmlReactParser(text)}>
            {icon}
        </Tooltip>
    );

    const getMembershipIcon = () => {
        const tooltipText = getMembershipTooltip(category.membership, categoryType);

        switch (category.membership) {
            case KmsTypeCategoryMembership.OPEN:
                return getIconWithTooltip(<Globe24Icon />, tooltipText);

            case KmsTypeCategoryMembership.RESTRICTED:
                return getIconWithTooltip(<NoAccess24Icon />, tooltipText);

            case KmsTypeCategoryMembership.PRIVATE:
                return getIconWithTooltip(<Lock24Icon />, tooltipText);

            case KmsTypeCategoryMembership.SHARED:
                return getIconWithTooltip(<Group24Icon />, tooltipText);

            case KmsTypeCategoryMembership.PUBLIC_RESTRICTED:
                return getIconWithTooltip(<Unlock24Icon />, tooltipText);

            case KmsTypeCategoryMembership.PUBLIC_OPEN:
                return getIconWithTooltip(<Visible24Icon />, tooltipText);

            default:
                return null;
        }
    };

    const getModerationIcon = () => {
        if (category.moderation) {
            const tooltipText = translate("Moderated - Content will only be available after moderator approval.");
            return getIconWithTooltip(<Expert24Icon />, tooltipText);
        }
        return null;
    };

    const icon = getMembershipIcon();
    const moderationIcon = getModerationIcon();

    return (
        <div className="kms-ds-publish-category-icon">
            {icon}
            {moderationIcon}
        </div>
    );
}
